<script lang="ts" setup>
import WidgetTrialExpiration from '@/components/widgets/WidgetTrialExpiration.vue'
import Logo from '@/components/Logo.vue'
import BusinessNav from '@/components/navigation/BusinessNavigation.vue'
import BottomNavigation from '@/components/navigation/BottomNavigation.vue'
import BannerPaymentFailed from '@/components/banners/BannerPaymentFailed.vue'

const businessStore = useBusinessStore()

const { smAndDown, mdAndDown } = useDisplay()

const showNav = ref(!smAndDown.value)

const route = useRoute()

const layoutMeta = computed(() => {
  return {
    sidebar: true,
    subNavType: null,
    ...route.meta.admin,
  }
})

const subNavType = computed(() => layoutMeta.value.subNavType)
const { navigationItems } = useSubNav(subNavType)

const isLoading = ref(false)

const { hasPaymentIssues, isTrialing } = storeToRefs(businessStore)
const contentWidthClass = computed(() => {
  const width = route.meta.admin?.contentWidth
  if (width === 'full') return 'content--full'
  if (width === 'wide') return 'content--wide'
  return ''
})

const containerGapClass = computed(() => {
  const gap = route.meta.admin?.sidebarGap
  if (gap === 'wide') return 'content-container--wide-gap'
  return ''
})

const isSidebarBelowContent = computed(() => route.meta.admin?.sidebarPlacement === 'bottom')

const contentColProps = computed(() =>
  isSidebarBelowContent.value ? { lg: 12, order: 1, orderLg: 1 } : { lg: 9, order: 3, orderLg: 2 }
)

const sidebarColProps = computed(() =>
  isSidebarBelowContent.value ? { lg: 12, order: 2, orderLg: 2 } : { lg: 3, order: 1, orderLg: 3 }
)

const isTrialWidget = computed(() => {
  if (!layoutMeta.value.sidebar) return false

  return isTrialing.value && !isLoading.value
})
</script>

<template>
  <v-main>
    <banner-payment-failed v-if="hasPaymentIssues" />
    <div v-if="smAndDown" class="px-4 pt-4 d-flex align-center">
      <v-app-bar-nav-icon :ripple="false" class="admin-layout__trigger" @click.stop="showNav = !showNav" />
      <nuxt-link :to="{ name: 'business-profile' }" class="text-no-decoration flex d-flex justify-center flex-grow-1">
        <logo data-cy="indexLogo" admin-logo-variant="dark" />
      </nuxt-link>
      <div class="pl-9" />
    </div>
    <business-nav v-model="showNav" :is-locked="businessStore.isTrialExpired || businessStore.isLocked" />
    <skr-spinner v-if="isLoading" fullscreen />
    <v-container v-else fluid class="content-container" :class="containerGapClass">
      <v-row>
        <v-col cols="12" v-bind="contentColProps" :class="['content', contentWidthClass]">
          <slot />
        </v-col>
        <v-col v-if="layoutMeta.sidebar" cols="12" v-bind="sidebarColProps">
          <template v-if="isTrialWidget && businessStore.trialEndDate">
            <widget-trial-expiration
              :full-width="mdAndDown || isSidebarBelowContent"
              :trial-end-date="businessStore.trialEndDate"
            />
            <hr v-if="mdAndDown" class="divider" />
          </template>
        </v-col>
      </v-row>
    </v-container>
    <bottom-navigation v-if="smAndDown" :items="navigationItems" />
  </v-main>
</template>

<style lang="sass" scoped>
.content-container
  --content-gap: #{6 * $bw}
  --content-gap-top: #{6 * $bw}
  padding: 6 * $bw
  padding-top: var(--content-gap-top)
  padding-left: var(--content-gap)
  padding-right: var(--content-gap)
  +miw(md)
    --content-gap-top: #{24 * $bw}

.content-container--wide-gap
  +miw(md)
    --content-gap: #{20 * $bw}

.content
  max-width: calc(#{$site-width-narrow} + #{($base-width * 3 * 2)})
  margin: 0 auto
  flex-grow: 1
  &--wide
    max-width: calc(#{$site-width} + #{($base-width * 3 * 2)})
  &--full
    max-width: none
    width: 100%
    :deep(.heading1 + .heading2)
      max-width: 750px
    +maw(sm)
      &
        :deep(.heading1 + .heading2)
          max-width: none

.divider
  border: 1px solid $c-grey-light
  margin: #{$base-width * 6} -#{$base-width * 6}

[class*='col-']:empty
  display: none

.admin-layout__trigger
  &:before
    left: 50%
    top: 50%
    transform: translate(-50%,-50%)
    width: 46px
    height: 46px
    border-radius: 50%
    background: $c-primary
  &.v-btn--icon.v-size--default
    border-radius: 0
  span
    i
      height: 26px !important
      font-size: 34px !important
      width: 26px !important
  &:hover
    span
      color: $c-primary
</style>
